import React, { useState, useEffect, useRef } from "react";
import Logo from "../Assets/CoPilot_Logo.svg";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Typography,
  AppBar,
  Toolbar,
  Box,
  Button,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import { useAuth } from "../Context/AuthProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { useGlobalState } from '../Context/GlobalContext';
import OrderEntry from "./OrderEntry";
import TradeEntryPopup from "./TradeEntry/TradeEntryPopup";
import { useBondCharacteristicStore } from "../Store/bondCharacteristicStore"; // <-- added import
import { usePortfolioRebalanceStore } from "../Store/portfolioRebalanceStore";
import { JOB_STATUS } from "../Utils/portfolioRebalanceJobStorage";
import { tokens, withAlpha } from "../Common/designTokens"; // NEW
import { isAdminRole } from "../Constants/AppConstants";

const NAVBAR_HEIGHT = '80px';

function NavBar() {
  const {rMode} = useGlobalState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [oeOpen, setOeOpen] = useState(false);
  const [tradeEntryOpen, setTradeEntryOpen] = useState(false);
  const [previousTab, setPreviousTab] = useState('');
  const navButtonsRef = useRef(null);
  const [selectedTab, setSelectedTab] = useState(() => {
    let path = window.location.pathname;
   
    if(path) {
      path = path.slice(1);
    }

    // if (path === '/order') return 'order';
    // if (path === '/inventory') return 'inventory';
    // return '';

  });
  const { logout, token: jwtToken, userProfile, role, userId, isPms, isOms } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { resetBondCharacteristicId } = useBondCharacteristicStore(); // <-- grab reset fn

  // Async rebalance: show a green dot on Portfolio Workbench when ANY account has
  // a result that is ready and not yet viewed.
  const rebalanceJobs = usePortfolioRebalanceStore((s) => s.rebalanceJobs);
  const hasRebalanceResultReady = Object.values(rebalanceJobs || {}).some(
    (j) => j?.status === JOB_STATUS.READY && !j?.seen
  );

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const handleOrderEntryClose = () => {
    setOeOpen(false);
    setSelectedTab(previousTab);
  };

  const handleOrderEntryClick = () => {
    setPreviousTab(selectedTab);
    setSelectedTab('');
    setOeOpen(true);
  };

  const handleTradeEntryClick = () => {
    setPreviousTab(selectedTab);
    setSelectedTab('');
    setTradeEntryOpen(true);
  };

  const handleTradeEntryClose = () => {
    setTradeEntryOpen(false);
    setSelectedTab(previousTab);
  };

  const handleTradeAdded = (newTrade) => {
    console.log('Trade added from navbar:', newTrade);
    // Trade is already added to store, just log for now
  };

  useEffect(() => {
    // a more efficient way to set a path for selecting a tab
    let path = location.pathname;
    
    if(path) {
      path = path.slice(1);
    }

    // Handle order preview screen as part of portfolio rebalance
    const tabMap = {
        'portfolio-rebalance': 'portfolio-rebalance',
        'portfolio-rebalance/order-preview': 'portfolio-rebalance',
        'order': 'order',
        'inventory': 'inventory',
        'sec-master': 'sec-master',
        'search-sec-master': 'sec-master',
      'watchlist': 'watchlist',
      'ma-inventory': 'watchlist',
        'inventory/yield-table': 'inventory',
        'counter-parties': 'counter-parties', // <-- add
        'quickfix-logs': 'quickfix-logs',
        'admin': 'admin',
        'session-user-management': 'admin',
        'edi-file-process-management': 'admin',
    };

    const newSelected = tabMap[path] || path;
    setSelectedTab(newSelected);

    // If navigating away from inventory, reset characteristic id
    if (newSelected !== 'inventory' && newSelected !== 'search-inventory') {
      resetBondCharacteristicId();
    }

    // if (path === '/order') {
    //   setSelectedTab('order');
    // } else if (path === '/inventory') {
    //   setSelectedTab('inventory');
    // } else {
    //   setSelectedTab('');
    // }

  }, [location.pathname]);

  useEffect(() => {
    const navEl = navButtonsRef.current;
    if (!navEl) return;

    const logLayout = (reason) => {
      const payload = {
        reason,
        navClientWidth: navEl.clientWidth,
        navScrollWidth: navEl.scrollWidth,
        viewportWidth: window.innerWidth,
        devicePixelRatio: window.devicePixelRatio,
        fontStatus: document.fonts ? document.fonts.status : 'unsupported',
      };
      // console.info('[NAVBAR_DEBUG] layout', payload);
    };

    const logIfOverflow = (reason) => {
      if (navEl.scrollWidth > navEl.clientWidth) {
        logLayout(reason);
      }
    };

    logLayout('init');
    logIfOverflow('init-overflow');

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        logLayout('fonts-ready');
        logIfOverflow('fonts-ready-overflow');
      });
    }

    const handleResize = () => logIfOverflow('window-resize-overflow');
    window.addEventListener('resize', handleResize);

    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => logIfOverflow('nav-resize-overflow'));
      resizeObserver.observe(navEl);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  const displayMode = rMode ? "none" : "flex";
  const orderEntryDisplayMode = rMode ? "none" : "block";
  
  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          height: NAVBAR_HEIGHT,
          width: "100vw",
          display: displayMode,
          top: 0,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          borderRadius: '0px',
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          height: '100%',
        }}>
          {/* Logo Section */}
          <Box sx={{ 
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px'
          }}>
            <img src={Logo} alt="Logo" style={{ height: "6vh", width: "auto" }} />
          </Box>

          {/* Content Section */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
          }}>
            <Toolbar sx={{
              width: '100%',
              display: "flex",
              justifyContent: "space-between",
              alignItems: 'center',
            }}>
              {/* Navigation Buttons */}
              <Box
                ref={navButtonsRef}
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: { xs: 1, sm: 2, md: 3 },
                  overflowX: "auto",
                  flexWrap: "nowrap",
                  minWidth: 0,
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  "& .MuiButton-root": {
                    flexShrink: 0,
                  },
                }}
              >
                <Button
                  color="inherit"
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    textTransform: "none",
                    padding: '6px 10px',
                    fontWeight: selectedTab === 'inventory' ? 'bold' : 'normal',
                    // selected: light rounded bg + primary-colored text
                    backgroundColor: selectedTab === 'inventory' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.9) : 'transparent',
                    color: selectedTab === 'inventory' ? tokens.palette.primary : withAlpha(tokens.colors.white, 0.8),
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: selectedTab === 'inventory' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.85) : withAlpha(tokens.colors.white, 0.06),
                    }
                  }}
                  onClick={() => {
                    if (selectedTab !== 'inventory') {
                      setSelectedTab('inventory');
                      navigate("/inventory");
                    }
                  }}
                >
                  Inventory
                </Button>
                <Button
                  color="inherit"
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    textTransform: "none",
                    padding: '6px 10px',
                    fontWeight: selectedTab === 'sec-master' ? 'bold' : 'normal',
                    backgroundColor: selectedTab === 'sec-master' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.9) : 'transparent',
                    color: selectedTab === 'sec-master' ? tokens.palette.primary : withAlpha(tokens.colors.white, 0.8),
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: selectedTab === 'sec-master' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.85) : withAlpha(tokens.colors.white, 0.06),
                    }
                  }}
                  onClick={() => {
                    if (selectedTab !== 'sec-master') {
                      setSelectedTab('sec-master');
                      navigate("/sec-master");
                    }
                  }}
                >
                  Sec Master
                </Button>
                <Button
                  color="inherit"
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    textTransform: "none",
                    padding: '6px 10px',
                    fontWeight: selectedTab === 'watchlist' ? 'bold' : 'normal',
                    backgroundColor: selectedTab === 'watchlist' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.9) : 'transparent',
                    color: selectedTab === 'watchlist' ? tokens.palette.primary : withAlpha(tokens.colors.white, 0.8),
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: selectedTab === 'watchlist' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.85) : withAlpha(tokens.colors.white, 0.06),
                    }
                  }}
                  onClick={() => {
                    if (selectedTab !== 'watchlist') {
                      setSelectedTab('watchlist');
                      navigate("/watchlist");
                    }
                  }}
                >
                  Watchlist
                </Button>
                <Button
                  color="inherit"
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    textTransform: "none",
                    padding: '6px 10px',
                    fontWeight: selectedTab === 'order' ? 'bold' : 'normal',
                    backgroundColor: selectedTab === 'order' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.9) : 'transparent',
                    color: selectedTab === 'order' ? tokens.palette.primary : withAlpha(tokens.colors.white, 0.8),
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: selectedTab === 'order' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.85) : withAlpha(tokens.colors.white, 0.06),
                    }
                   }}
                   onClick={() => {
                     if (selectedTab !== 'order') {
                       setSelectedTab('order');
                       navigate("/order");
                     }
                   }}
                 >
                   Order Blotter
                 </Button>
                
                {/* <Button
                  color="inherit"
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    textTransform: "none",
                    padding: '6px 10px',
                    fontWeight: selectedTab === 'order-entry' ? 'bold' : 'normal',
                    backgroundColor: selectedTab === 'order-entry' ? 'rgba(245, 166, 35, 1)' : 'transparent',
                    color: selectedTab === 'order-entry' ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 0.8)',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: selectedTab === 'order-entry' ? 'rgba(245, 166, 35, 0.9)' : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                  onClick={() => {
                    if (selectedTab !== 'order-entry') {
                      setSelectedTab('order-entry');
                      navigate("/order-entry");
                    }
                  }}
                >
                  Order Entry Page
                </Button>                 */}

                <Button
                  color="inherit"
                  sx={{
                    display: orderEntryDisplayMode,
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    textTransform: "none",
                    padding: '6px 10px',
                    fontWeight: oeOpen ? 'bold' : 'normal',
                    backgroundColor: oeOpen ? withAlpha(tokens.colors.bodyBackgroundColor, 0.9) : 'transparent',
                    color: oeOpen ? tokens.palette.primary : withAlpha(tokens.colors.white, 0.8),
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: oeOpen ? withAlpha(tokens.colors.bodyBackgroundColor, 0.85) : withAlpha(tokens.colors.white, 0.06),
                    }
                   }}
                   onClick={handleOrderEntryClick}
                 >
                   Order Entry
                 </Button>
                <Button
                  color="inherit"
                  sx={{
                    display: orderEntryDisplayMode,
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    textTransform: "none",
                    padding: '6px 10px',
                    fontWeight: tradeEntryOpen ? 'bold' : 'normal',
                    backgroundColor: tradeEntryOpen ? withAlpha(tokens.colors.bodyBackgroundColor, 0.9) : 'transparent',
                    color: tradeEntryOpen ? tokens.palette.primary : withAlpha(tokens.colors.white, 0.8),
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: tradeEntryOpen ? withAlpha(tokens.colors.bodyBackgroundColor, 0.85) : withAlpha(tokens.colors.white, 0.06),
                    }
                  }}
                  onClick={handleTradeEntryClick}
                >
                  Trade Entry
                </Button>
                {/* Show Position Explorer only if isPms flag is enabled */}
                {isPms && (
                  <Button
                    color="inherit"
                    sx={{
                      fontFamily: 'Lato',
                      fontSize: '14px',
                      lineHeight: '18px',
                      letterSpacing: '0%',
                      textTransform: "none",
                      padding: '6px 10px',
                      fontWeight: selectedTab === 'account-positions' ? 'bold' : 'normal',
                      // changed: use same active bg & primary text as other buttons
                      backgroundColor: selectedTab === 'account-positions' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.9) : 'transparent',
                      color: selectedTab === 'account-positions' ? tokens.palette.primary : withAlpha(tokens.colors.white, 0.8),
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: selectedTab === 'account-positions' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.85) : withAlpha(tokens.colors.white, 0.06),
                      }
                   }}
                    onClick={() => {
                      if (selectedTab !== 'account-positions') {
                        setSelectedTab('account-positions');
                        navigate("/account-positions");
                      }
                    }}
                  >
                    Position Explorer
                  </Button>
                )}
                {/* Show Rebalance Workbench only if isPms flag is enabled */}
                {isPms && (
                  <Button
                    color="inherit"
                   sx={{
                      fontFamily: 'Lato',
                      fontSize: '14px',
                      lineHeight: '18px',
                      letterSpacing: '0%',
                      textTransform: "none",
                      padding: '6px 10px',
                      fontWeight: selectedTab === 'portfolio-rebalance' ? 'bold' : 'normal',
                      backgroundColor: selectedTab === 'portfolio-rebalance' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.9) : 'transparent',
                      color: selectedTab === 'portfolio-rebalance' ? tokens.palette.primary : withAlpha(tokens.colors.white, 0.8),
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: selectedTab === 'portfolio-rebalance' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.85) : withAlpha(tokens.colors.white, 0.06),
                      }
                    }}
                    onClick={() => {
                      if (selectedTab !== 'portfolio-rebalance') {
                        setSelectedTab('portfolio-rebalance');
                        navigate("/portfolio-rebalance");
                      }
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: "inline-flex", alignItems: "center", gap: "2px" }}
                    >
                      Portfolio Workbench
                      {hasRebalanceResultReady && (
                        <Box
                          component="span"
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: tokens.colors.buyFontColor,
                            display: "inline-block",
                          }}
                        />
                      )}
                    </Box>
                  </Button>
                )}
                <Button
                  color="inherit"
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    textTransform: "none",
                    padding: '6px 10px',
                    fontWeight: selectedTab === 'counter-parties' ? 'bold' : 'normal',
                    backgroundColor: selectedTab === 'counter-parties' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.9) : 'transparent',
                    color: selectedTab === 'counter-parties' ? tokens.palette.primary : withAlpha(tokens.colors.white, 0.8),
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: selectedTab === 'counter-parties' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.85) : withAlpha(tokens.colors.white, 0.06),
                    }
                  }}
                  onClick={() => {
                    if (selectedTab !== 'counter-parties') {
                      setSelectedTab('counter-parties');
                      navigate("/counter-parties");
                    }
                  }}
                >
                  CPTYs
                </Button>
                <Button
                  color="inherit"
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    textTransform: "none",
                    padding: '6px 10px',
                    fontWeight: selectedTab === 'quickfix-logs' ? 'bold' : 'normal',
                    backgroundColor: selectedTab === 'quickfix-logs' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.9) : 'transparent',
                    color: selectedTab === 'quickfix-logs' ? tokens.palette.primary : withAlpha(tokens.colors.white, 0.8),
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: selectedTab === 'quickfix-logs' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.85) : withAlpha(tokens.colors.white, 0.06),
                    }
                  }}
                  onClick={() => {
                    if (selectedTab !== 'quickfix-logs') {
                      setSelectedTab('quickfix-logs');
                      navigate("/quickfix-logs");
                    }
                  }}
                >
                  QuickFIX Logs
                </Button>
                {/* Show Admin only for ADMIN/SUPER_ADMIN roles */}
                {isAdminRole(role) && (
                  <Button
                    color="inherit"
                    sx={{
                      fontFamily: 'Lato',
                      fontSize: '14px',
                      lineHeight: '18px',
                      letterSpacing: '0%',
                      textTransform: "none",
                      padding: '6px 10px',
                      fontWeight: selectedTab === 'admin' ? 'bold' : 'normal',
                      backgroundColor: selectedTab === 'admin' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.9) : 'transparent',
                      color: selectedTab === 'admin' ? tokens.palette.primary : withAlpha(tokens.colors.white, 0.8),
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: selectedTab === 'admin' ? withAlpha(tokens.colors.bodyBackgroundColor, 0.85) : withAlpha(tokens.colors.white, 0.06),
                      }
                    }}
                    onClick={() => {
                      if (selectedTab !== 'admin') {
                        setSelectedTab('admin');
                        navigate("/admin");
                      }
                    }}
                  >
                    Admin
                  </Button>
                )}
              </Box>
              
              {/* Profile Section - Rearranged */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  color="inherit"
                  onClick={handleProfileClick}
                  endIcon={<KeyboardArrowDownIcon sx={{height: '18px', width: '18px'}} />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    '&:hover': {
                      // backgroundColor: tokens.colors.gray
                    },
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <AccountCircleOutlinedIcon sx={{ height: '30px', width: '30px', mr: 1 }} />
                  <Box sx={{ 
                    height: '25px',
                    
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'space-between', // Vertically center content
                    alignItems: 'flex-start' 
                  }}>
                    <Typography variant="body1" sx={{ 
                      fontSize: '14px', 
                      // fontWeight: 'bold', 
                      color: tokens.colors.white,
                      lineHeight: 0.6 ,
                      // pb: '2px',
                    }}>
                      {userProfile?.userName || ''}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      fontSize: '10px', 
                      color: tokens.colors.white,
                      lineHeight: 1.2,
                    }}>
                      {userProfile?.role || ''}
                    </Typography>
                  </Box>
                </Button>
              </Box>
            </Toolbar>
          </Box>
        </Box>
      </AppBar>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{
          p: 0,
         
        }}
        MenuListProps={{
          sx: {
       // Specifically override padding-top
            paddingBottom: 0,    // Specifically override padding-bottom
          }
        }}
        PaperProps={{
          sx: {
            pb: '0px',
            padding: "0px , 0px",
            backgroundColor: 'rgba(38, 38, 38, 1)',
            width: 'auto', // Make width dynamic
            // minWidth: '240px', // Set minimum width
            // maxWidth: '400px', // Set maximum width to prevent extremely long emails from expanding too much
          borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          }
        }}
      >
        <Box 
          sx={{ 
            padding: '0px 10px',
            '&:hover': {
              cursor: 'default'
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ 
            pb: '8px',
            display: 'flex', 
            flexDirection: 'column', 
            width: '100%',
            justifyContent: 'center'
          }}>
            {/* Email without tooltip and ellipsis */}
            {userProfile?.email && (
              <Typography
                variant="body1"
                sx={{
                  fontSize: '16px',
                  opacity: 0.8,
                  cursor: 'default',
                  // paddingTop: '8px', // Add some padding at the top
                }}
              >
                {userProfile.email}
              </Typography>
            )}
            <Typography variant="caption" sx={{ 
              fontSize: '12px',
              opacity: 0.8
            }}>
              {userProfile?.role || ''}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
        <MenuItem onClick={handleLogout} sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center', // Add this to vertically center
          padding: '8px 10px', // Change to single padding value
          height: '36px', // Add fixed height for consistency
        }}>
          <Typography sx={{ 
            fontSize: '16px',
            lineHeight: 1, // Add this to help with vertical centering
          }}>
            Logout
          </Typography>
          <LogoutIcon fontSize="small" />
        </MenuItem>
      </Menu>
      <OrderEntry
        readOnly={false}
        open={oeOpen}
        onClose={handleOrderEntryClose}
        source="navbar"
        token={jwtToken}
      />
      <TradeEntryPopup
        open={tradeEntryOpen}
        onClose={handleTradeEntryClose}
        // onTradeAdded={handleTradeAdded}
      />
    </>
  );
}

export default NavBar;
